import React, { useMemo, useState } from 'react';
import { getDeploymentComponentLabels, normalizeAssetId } from '../tracking/trackingModel';
import { isLegacyBundleAsset } from '../tracking/deploymentUsageModel';
import QrScanner from '../tracking/QrScanner';
import '../tracking/tracking.css';
import './admin.css';

function clean(value) {
  return String(value ?? '').trim();
}

function timeValue(value) {
  const parsed = Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDateTime(value) {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return clean(value) || 'Not recorded';
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(value) {
  if (!value) return 'Not recorded';
  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return clean(value) || 'Not recorded';
  return parsed.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusClass(value) {
  return clean(value || 'available').toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function returnStatusClass(condition) {
  const key = clean(condition || 'available').toLowerCase();
  return key === 'ready' ? 'available' : statusClass(key);
}

function statusLabel(asset) {
  const status = clean(asset?.status || 'available').replace(/_/g, ' ');
  const condition = clean(asset?.condition || 'ready').replace(/_/g, ' ');
  if (status === condition || condition === 'ready') return status;
  return `${status} · ${condition}`;
}

function eventFor(events, type, assetId, deploymentId) {
  const id = normalizeAssetId(assetId);
  return (events || [])
    .filter(item => item?.type === type)
    .filter(item => normalizeAssetId(item?.assetId) === id)
    .filter(item => !deploymentId || item?.deploymentId === deploymentId)
    .sort((a, b) => timeValue(b?.timestamp) - timeValue(a?.timestamp))[0] || null;
}

function buildAssignments(state, asset) {
  const assetId = normalizeAssetId(asset?.id);
  return (state?.deployments || [])
    .filter(deployment => (deployment?.assetIds || []).map(normalizeAssetId).includes(assetId))
    .map(deployment => {
      const checkout = eventFor(state?.events, 'asset_checkout', assetId, deployment.id);
      const returned = eventFor(state?.events, 'asset_return', assetId, deployment.id);
      const components = getDeploymentComponentLabels(deployment, asset);
      const checkoutAt = checkout?.timestamp || deployment?.createdAt || deployment?.startDate || '';
      const returnAt = returned?.timestamp || deployment?.returnedAt || '';
      return {
        deployment,
        checkout,
        returned,
        components,
        checkoutAt,
        returnAt,
        sortAt: timeValue(checkoutAt) || timeValue(deployment?.startDate),
      };
    })
    .sort((a, b) => b.sortAt - a.sortAt);
}

export default function AssetHistory({ state }) {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');

  const assets = useMemo(() => (state?.assets || [])
    .filter(asset => !isLegacyBundleAsset(asset))
    .sort((a, b) => clean(a?.name).localeCompare(clean(b?.name))), [state?.assets]);

  const normalizedQuery = normalizeAssetId(query);
  const searchText = clean(query).toLowerCase();
  const matches = useMemo(() => {
    if (!searchText) return assets.slice(0, 30);
    return assets.filter(asset => {
      const id = normalizeAssetId(asset?.id);
      return id === normalizedQuery
        || clean(asset?.name).toLowerCase().includes(searchText)
        || clean(asset?.category).toLowerCase().includes(searchText)
        || id.toLowerCase().includes(searchText);
    }).slice(0, 30);
  }, [assets, normalizedQuery, searchText]);

  const selected = assets.find(asset => normalizeAssetId(asset?.id) === normalizeAssetId(selectedId)) || null;
  const assignments = useMemo(() => selected ? buildAssignments(state, selected) : [], [state, selected]);
  const currentDeployment = selected?.currentDeploymentId
    ? (state?.deployments || []).find(item => item?.id === selected.currentDeploymentId) || null
    : null;
  const lastAssignment = assignments[0] || null;

  const chooseAsset = asset => {
    if (!asset) return;
    const id = normalizeAssetId(asset.id);
    setSelectedId(id);
    setQuery(asset.id);
  };

  const chooseExact = event => {
    event.preventDefault();
    const exact = assets.find(asset => normalizeAssetId(asset?.id) === normalizedQuery)
      || matches[0]
      || null;
    chooseAsset(exact);
  };

  const handleScan = value => {
    const id = normalizeAssetId(value);
    if (!id) return;
    setQuery(id);
    const exact = assets.find(asset => normalizeAssetId(asset?.id) === id) || null;
    setSelectedId(exact ? id : '');
  };

  return (
    <>
      <section className="admin-card asset-history-search-card">
        <div className="admin-section-head">
          <div>
            <h2>Asset History</h2>
            <p>Search an asset ID, scan/tap a gear tag, paste a gear-tag link, or search by asset name/category to see chain of custody.</p>
          </div>
          <div className="admin-day-pill">{assets.length} tracked assets</div>
        </div>

        <form className="asset-history-search" onSubmit={chooseExact}>
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="LM-PACK-01, Radio Clamshell, or gear tag URL"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck="false"
          />
          <button type="submit">Find asset</button>
        </form>

        <div className="asset-history-scanner">
          <QrScanner
            onScan={handleScan}
            label="Scan a QR code or tap an NFC gear tag to open that asset’s chain-of-custody history."
          />
        </div>

        <div className="asset-history-results">
          {matches.map(asset => {
            const id = normalizeAssetId(asset?.id);
            const active = selected && normalizeAssetId(selected.id) === id;
            return (
              <button
                type="button"
                key={id}
                className={active ? 'is-selected' : ''}
                onClick={() => chooseAsset(asset)}
              >
                <strong>{asset.name}</strong>
                <small>{id} · {asset.category || 'Other'} · {statusLabel(asset)}</small>
              </button>
            );
          })}
          {!matches.length && <div className="admin-empty">No matching tracked asset.</div>}
        </div>
      </section>

      {selected && (
        <>
          <div className="admin-summary-grid asset-history-summary">
            <div className="admin-stat"><span>Asset</span><strong>{selected.name}</strong><small>{normalizeAssetId(selected.id)}</small></div>
            <div className="admin-stat"><span>Current Status</span><strong className={`admin-status admin-status-${statusClass(selected.status)}`}>{statusLabel(selected)}</strong><small>{selected.active === false ? 'Inactive asset' : 'Tracked asset'}</small></div>
            <div className="admin-stat"><span>{currentDeployment ? 'Assigned To' : 'Last Assigned To'}</span><strong>{currentDeployment?.person || lastAssignment?.deployment?.person || 'No assignment recorded'}</strong><small>{currentDeployment?.incident || lastAssignment?.deployment?.incident || '—'}</small></div>
            <div className="admin-stat"><span>Assignments</span><strong>{assignments.length}</strong><small>Permanent deployment history</small></div>
          </div>

          {currentDeployment && (
            <section className="admin-card asset-history-current">
              <div className="admin-section-head">
                <div>
                  <h2>Currently Assigned</h2>
                  <p>This asset has not been returned from the deployment below.</p>
                </div>
                <span className="admin-status admin-status-deployed">Deployed</span>
              </div>
              <div className="admin-table-row"><span><strong>{currentDeployment.person}</strong><small>Member</small></span><strong>{currentDeployment.incident}</strong></div>
              <div className="admin-table-row"><span><strong>{currentDeployment.deploymentType || currentDeployment.role || 'EMPF'}</strong><small>Deployment type</small></span><strong>{formatDate(currentDeployment.startDate)}</strong></div>
            </section>
          )}

          <section className="admin-card">
            <div className="admin-section-head">
              <div>
                <h2>Assignment History</h2>
                <p>Newest first. Checkout and return events are preserved after the asset becomes available again.</p>
              </div>
            </div>

            {!assignments.length && <div className="admin-empty">This asset has no recorded deployment assignments yet.</div>}
            <div className="asset-history-timeline">
              {assignments.map(({ deployment, checkout, returned, components, checkoutAt, returnAt }) => (
                <article className="admin-deployment-card asset-history-entry" key={deployment.id}>
                  <div className="admin-deployment-title">
                    <div>
                      <strong>{deployment.person || 'Unknown member'}</strong>
                      <span>{deployment.incident || 'Incident not recorded'}{deployment.incidentOrderNumber ? ` · ${deployment.incidentOrderNumber}` : ''}</span>
                    </div>
                    <span className={`admin-status admin-status-${returned ? returnStatusClass(returned.condition) : deployment.status === 'closed' ? 'available' : 'deployed'}`}>
                      {returned ? clean(returned.condition || 'returned').replace(/_/g, ' ') : deployment.status === 'closed' ? 'returned' : 'deployed'}
                    </span>
                  </div>

                  <div className="asset-history-grid">
                    <div><span>CHECKED OUT</span><strong>{formatDateTime(checkoutAt)}</strong><small>{checkout?.actor ? `Recorded by ${checkout.actor}` : 'Checkout actor not separately recorded'}</small></div>
                    <div><span>RETURNED</span><strong>{returnAt ? formatDateTime(returnAt) : 'Still assigned / no return event'}</strong><small>{returned?.actor ? `Returned by ${returned.actor}` : '—'}</small></div>
                    <div><span>DEPLOYMENT</span><strong>{deployment.deploymentType || deployment.role || 'EMPF'}</strong><small>Start date: {formatDate(deployment.startDate)}</small></div>
                    <div><span>RETURN CONDITION</span><strong>{returned ? clean(returned.condition || 'ready').replace(/_/g, ' ') : 'Not recorded'}</strong><small>{returned?.deployedTo ? `Was assigned to ${returned.deployedTo}` : '—'}</small></div>
                  </div>

                  {components.length > 0 && (
                    <div className="admin-gear-tags asset-history-components">
                      {components.map(component => <span key={component}>{component}</span>)}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </>
  );
}
